// Portal Auth Context
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import portalApi from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Workspace context
  const [workspaces, setWorkspaces] = useState([]);
  const [workspacesLoaded, setWorkspacesLoaded] = useState(false);
  const [activeWorkspaceId, setActiveWorkspaceIdState] = useState(
    () => localStorage.getItem('calyvra_active_workspace') || null
  );

  const loadWorkspaces = useCallback(async () => {
    try {
      const data = await portalApi.getWorkspaces();
      const list = data.workspaces || [];
      setWorkspaces(list);
      // Reset active if it's no longer accessible
      setActiveWorkspaceIdState((prev) => {
        if (prev && !list.some((w) => w.id === prev)) {
          localStorage.removeItem('calyvra_active_workspace');
          return null;
        }
        return prev;
      });
    } catch {
      setWorkspaces([]);
    } finally {
      setWorkspacesLoaded(true);
    }
  }, []);

  const setActiveWorkspace = useCallback((id) => {
    if (id) localStorage.setItem('calyvra_active_workspace', id);
    else localStorage.removeItem('calyvra_active_workspace');
    setActiveWorkspaceIdState(id);
  }, []);

  const checkAuth = useCallback(async () => {
    const token = portalApi.getToken();
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const userData = await portalApi.getMe();
      setUser(userData);
    } catch (err) {
      portalApi.setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (user) loadWorkspaces();
    else { setWorkspaces([]); setWorkspacesLoaded(false); }
  }, [user, loadWorkspaces]);

  // Step 1 of login: returns { mfa_setup_required } or { mfa_required }.
  const login = async (email, password) => {
    setError(null);
    return portalApi.login(email, password);
  };

  const logout = async () => {
    try {
      await portalApi.logout();
    } finally {
      setUser(null);
      localStorage.removeItem('calyvra_active_workspace');
      setActiveWorkspaceIdState(null);
      window.location.href = '/portal/login';
    }
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return user.permissions?.includes(permission);
  };

  const hasAnyPermission = (permissions) => {
    return permissions.some(p => hasPermission(p));
  };

  const canAccessYear = (yearId) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    if (!user.year_access || user.year_access.length === 0) return true;
    return user.year_access.includes(yearId);
  };

  const isCalyvraAdmin = user?.role === 'admin' || (user?.permissions || []).includes('workspaces.manage');
  const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId) || null;

  const viewAsUser = async (userId) => {
    await portalApi.impersonate(userId);
    localStorage.removeItem('calyvra_active_workspace');
    window.location.href = '/portal';
  };

  const exitViewAs = () => {
    portalApi.stopImpersonation();
    localStorage.removeItem('calyvra_active_workspace');
    window.location.href = '/portal';
  };

  const isImpersonating = portalApi.isImpersonating();
  const impersonationName = portalApi.impersonationName();

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      loading,
      error,
      login,
      logout,
      hasPermission,
      hasAnyPermission,
      canAccessYear,
      checkAuth,
      workspaces,
      workspacesLoaded,
      activeWorkspace,
      activeWorkspaceId,
      setActiveWorkspace,
      loadWorkspaces,
      isCalyvraAdmin,
      viewAsUser,
      exitViewAs,
      isImpersonating,
      impersonationName,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
